import React, { useEffect } from 'react';
import { CheckCircle, AlertCircle, Info, X } from 'lucide-react';

export type ToastType = 'success' | 'error' | 'info';

export interface ToastMessage {
  id: string;
  type: ToastType;
  title: string;
  message: string;
}

interface ToastProps {
  toasts: ToastMessage[];
  onDismiss: (id: string) => void;
}

export const ToastContainer: React.FC<ToastProps> = ({ toasts, onDismiss }) => {
  return (
    <div className="fixed bottom-6 right-6 z-[9999] flex flex-col gap-3 pointer-events-none">
      {toasts.map((toast) => (
        <ToastItem key={toast.id} toast={toast} onDismiss={onDismiss} />
      ))}
    </div>
  );
};

const ToastItem: React.FC<{ toast: ToastMessage; onDismiss: (id: string) => void }> = ({ toast, onDismiss }) => {
  useEffect(() => {
    const timer = setTimeout(() => {
      onDismiss(toast.id);
    }, 5000); // Auto dismiss after 5s
    return () => clearTimeout(timer);
  }, [toast.id, onDismiss]);

  // Use theme colors for success/info, keep red for errors (semantic meaning)
  const styles = {
    success: 'bg-cc-elevated border-cc-accent text-cc-accent',
    error: 'bg-cc-elevated border-red-500 text-red-600 dark:text-red-400',
    info: 'bg-cc-elevated border-cc-accent text-cc-accent',
  };

  const icons = {
    success: CheckCircle,
    error: AlertCircle,
    info: Info,
  };

  const Icon = icons[toast.type];

  return (
    <div className={`pointer-events-auto w-80 p-4 rounded-xl shadow-2xl border-l-4 flex items-start gap-3 animate-in slide-in-from-right-10 fade-in duration-300 ${styles[toast.type]}`}>
      <Icon size={20} className="flex-shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <h4 className="text-sm font-bold text-cc-text leading-tight">{toast.title}</h4>
        <p className="text-xs text-cc-text-muted mt-1 leading-snug">{toast.message}</p>
      </div>
      <button onClick={() => onDismiss(toast.id)} className="text-cc-text-muted hover:text-cc-text">
        <X size={16} />
      </button>
    </div>
  );
};